import React, { useEffect, useState, useContext } from "react";
import axios from "axios";
import { AuthContext } from "../../context/AuthContext";
import { useNavigate, useParams } from "react-router-dom";
import Loader from "../common/Loader";
import API_BASE_URL from "../../config/api";

// ✅ React Icons
import {
  FiMail,
  FiPhone,
  FiMapPin,
  FiUsers,
  FiBriefcase,
  FiCalendar,
  FiCheckCircle,
} from "react-icons/fi";

const EmployeeProfile = () => {
  const { token } = useContext(AuthContext);
  const [employee, setEmployee] = useState(null);
  const navigate = useNavigate();
  const { id } = useParams();
  const sessionEmployeeId = sessionStorage.getItem("employeeId");
  const employeeId = id || sessionEmployeeId;

  // Show back button only if employeeId came from the URL
  const showBackButton = id && id !== sessionEmployeeId;

  useEffect(() => {
    if (!employeeId) return;

    const fetchEmployee = async () => {
      try {
        const res = await axios.get(
          `${API_BASE_URL}/api/employee/profile/${employeeId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        setEmployee(res.data);
      } catch (err) {
        console.error("Failed to load profile:", err);
      }
    };

    fetchEmployee();
  }, [employeeId, token]);


  const downloadFile = async (filePath) => {
    const response = await fetch(`${API_BASE_URL}/api/Employee/download/${filePath}`, {
      method: "GET",
      // headers: { Authorization: `Bearer ${token}` } // if needed
    });

    if (!response.ok) throw new Error("File not found or server error");

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = filePath.split("/").pop();
    document.body.appendChild(link);
    link.click();

    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };


  if (!employee) {
    return <Loader message="Loading profile..." />;
  }


  return (
    <div
      className="relative flex size-full min-h-screen flex-col bg-white overflow-x-hidden"
      style={{ fontFamily: 'Inter, "Noto Sans", sans-serif' }}
    >
      {showBackButton && (
        <button
          onClick={() => navigate("/app/employees")}
          className="absolute top-4 right-4 z-10 px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg text-sm font-medium shadow"
        >
          ← Back
        </button>
      )}
      <div className="layout-container flex h-full grow flex-col">
        <div className="gap-1 flex flex-1 justify-center py-5">
          <div className="layout-content-container flex flex-col w-100">
            <div className="flex p-4">
              <div className="flex w-full flex-col gap-4 items-center">
                <div className="flex gap-4 flex-col items-center">
                  <div
                    className="bg-center bg-no-repeat aspect-square bg-cover rounded-full min-h-32 w-32"
                    style={{
                      backgroundImage: `url('${employee.avatar
                        ? `${API_BASE_URL}/uploads/${employee.avatar}`
                        : 'https://cdn.pixabay.com/photo/2015/10/05/22/37/blank-profile-picture-973460_960_720.png' 
                      }')`,
                    }}
                  ></div>
                  <div className="flex flex-col items-center justify-center">
                    <p className="text-[#111518] text-[22px] font-bold leading-tight tracking-[-0.015em] text-center">
                      {employee.fullName}
                    </p>
                    <p className="text-[#637988] text-base font-normal leading-normal text-center">
                      {employee.jobTitle}
                    </p>
                    <p className="text-[#637988] text-base font-normal leading-normal text-center">
                      Employee ID: {employee.employeeID}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <h3 className="text-[#111518] text-lg font-bold leading-tight tracking-[-0.015em] px-4 pb-2 pt-4">
              Contact
            </h3>

            <ContactItem
              icon={<FiMail size={24} />}
              label="Email"
              value={employee.officialEmail}
            />
            <ContactItem
              icon={<FiPhone size={24} />}
              label="Phone"
              value={employee.phoneNumber}
            />
            <ContactItem
              icon={<FiMapPin size={24} />}
              label="Address"
              value={employee.address}
            />

            <h3 className="text-[#111518] text-lg font-bold leading-tight tracking-[-0.015em] px-4 pb-2 pt-4">
              Job Details
            </h3>

            <JobDetailItem
              icon={<FiUsers size={24} />}
              label="Department"
              value={employee.department}
            />
            <JobDetailItem
              icon={<FiBriefcase size={24} />}
              label="Position"
              value={employee.jobTitle}
            />
            <JobDetailItem
              icon={<FiCalendar size={24} />}
              label="Hire Date"
              value={new Date(employee.hireDate).toLocaleDateString("en-GB")}
            />
            <JobDetailItem
              icon={<FiCheckCircle size={24} />}
              label="Status"
              value={employee.status}
            />
          </div>

          {/* Employee Details Section */}
          <div className="layout-content-container flex flex-col w-100 flex-1">
            <div className="flex flex-wrap justify-between gap-3 p-4">
              <p className="text-[#111518] tracking-light text-[32px] font-bold leading-tight min-w-72">
                Employee Details
              </p>
            </div>

            <h2 className="text-[#111518] text-[22px] font-bold leading-tight tracking-[-0.015em] px-4 pb-3 pt-5">
              Personal Information
            </h2>

            <div className="p-4 grid grid-cols-2">
              <InfoItem
                label="Full Name"
                value={`${employee.fullName}`}
                right
              />
              <InfoItem
                label="Date of Birth"
                value={
                  employee.dob
                    ? new Date(employee.dob).toLocaleDateString("en-GB")
                    : "N/A"
                }
              />
              <InfoItem label="Gender" value={employee.gender} right />
              <InfoItem label="Nationality" value={employee.nationality} />
              <InfoItem
                label="Marital Status"
                value={employee.maritalStatus}
                right
              />
              <InfoItem label="Blood Group" value={employee.bloodGroup} />
            </div>

            <h2 className="text-[#111518] text-[22px] font-bold leading-tight tracking-[-0.015em] px-4 pb-3 pt-5">
              Documents
            </h2>

            <div className="px-4 py-3">
              <div className="overflow-x-auto rounded-xl border border-[#dce1e5] bg-white">
                <table className="min-w-full">
                  <thead>
                    <tr className="bg-white">
                      <th className="px-4 py-3 text-left text-[#111518] text-sm font-bold leading-normal w-[400px]">
                        Document Name
                      </th>
                      <th className="px-4 py-3 text-left text-[#111518] text-sm font-bold leading-normal w-[400px]">
                        Uploaded Date
                      </th>
                      <th className="px-4 py-3 text-left text-[#111518] text-sm font-bold leading-normal w-60">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {employee.attachments && employee.attachments.length > 0 ? (
                      employee.attachments.map((doc, index) => (
                        <tr key={index} className="border-t border-[#dce1e5]">
                          <td className="h-[72px] px-4 py-2 text-[#111518] text-sm font-normal leading-normal w-[400px]">
                            {doc.fileName}
                          </td>
                          <td className="h-[72px] px-4 py-2 text-[#637988] text-sm font-normal leading-normal w-[400px]">
                            {doc.uploadDate
                              ? new Date(doc.uploadDate).toLocaleDateString("en-GB")
                              : ""}
                          </td>
                          <td className="h-[72px] px-4 py-2 w-60">
                            <a
                              onClick={() => downloadFile(doc.filePath)}
                              download
                              className="text-blue-600 hover:underline text-sm font-medium cursor-pointer"
                            >
                              Download
                            </a>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td
                          colSpan="3"
                          className="px-4 py-3 text-center text-[#637988] text-sm"
                        >
                          No documents uploaded.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <h2 className="text-[#111518] text-[22px] font-bold leading-tight tracking-[-0.015em] px-4 pb-3 pt-5">
              Emergency Contact
            </h2>

            <div className="px-4 py-3">
              <div className="overflow-x-auto rounded-xl border border-[#dce1e5] bg-white">
                <table className="min-w-full">
                  <thead>
                    <tr className="bg-white">
                      <th className="px-4 py-3 text-left text-[#111518] text-sm font-bold leading-normal w-[400px]">
                        Name
                      </th>
                      <th className="px-4 py-3 text-left text-[#111518] text-sm font-bold leading-normal w-[400px]">
                        Relationship
                      </th>
                      <th className="px-4 py-3 text-left text-[#111518] text-sm font-bold leading-normal w-[400px]">
                        Address
                      </th>
                      <th className="px-4 py-3 text-left text-[#111518] text-sm font-bold leading-normal w-[400px]">
                        Contact No
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {employee.emergencyContacts &&
                      employee.emergencyContacts.length > 0 ? (
                      employee.emergencyContacts.map((emr, index) => (
                        <tr key={index} className="border-t border-[#dce1e5]">
                          <td className="h-[72px] px-4 py-2 text-[#637988] text-sm font-normal leading-normal w-[400px]">
                            {emr.name}
                          </td>
                          <td className="h-[72px] px-4 py-2 text-[#637988] text-sm font-normal leading-normal w-[400px]">
                            {emr.relationship}
                          </td>
                          <td className="h-[72px] px-4 py-2 text-[#637988] text-sm font-normal leading-normal w-[400px]">
                            {emr.address}
                          </td>
                          <td className="h-[72px] px-4 py-2 text-[#637988] text-sm font-normal leading-normal w-[400px]">
                            {emr.contactNumber}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td
                          colSpan="4"
                          className="px-4 py-3 text-center text-[#637988] text-sm"
                        >
                          No emergency contacts found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <h2 className="text-[#111518] text-[22px] font-bold leading-tight tracking-[-0.015em] px-4 pb-3 pt-5">
              Work Experience
            </h2>

            <div className="px-4 py-3">
              <div className="overflow-x-auto rounded-xl border border-[#dce1e5] bg-white">
                <table className="min-w-full">
                  <thead>
                    <tr className="bg-white">
                      <th className="px-4 py-3 text-left text-[#111518] text-sm font-bold leading-normal w-[400px]">
                        Company
                      </th>
                      <th className="px-4 py-3 text-left text-[#111518] text-sm font-bold leading-normal w-[400px]">
                        Position
                      </th>
                      <th className="px-4 py-3 text-left text-[#111518] text-sm font-bold leading-normal w-[400px]">
                        From Date
                      </th>
                      <th className="px-4 py-3 text-left text-[#111518] text-sm font-bold leading-normal w-[400px]">
                        To Date
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {employee.workExperience &&
                      employee.workExperience.length > 0 ? (
                      employee.workExperience.map((job, index) => (
                        <tr key={index} className="border-t border-[#dce1e5]">
                          <td className="h-[72px] px-4 py-2 text-[#637988] text-sm font-normal leading-normal w-[400px]">
                            {job.company}
                          </td>
                          <td className="h-[72px] px-4 py-2 text-[#637988] text-sm font-normal leading-normal w-[400px]">
                            {job.jobTitle}
                          </td>
                          <td className="h-[72px] px-4 py-2 text-[#637988] text-sm font-normal leading-normal w-[400px]">
                            {job.startDate
                              ? new Date(job.startDate).toLocaleDateString("en-GB")
                              : ""}
                          </td>
                          <td className="h-[72px] px-4 py-2 text-[#637988] text-sm font-normal leading-normal w-[400px]">
                            {job.endDate
                              ? new Date(job.endDate).toLocaleDateString("en-GB")
                              : ""}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td
                          colSpan="4"
                          className="px-4 py-3 text-center text-[#637988] text-sm"
                        >
                          No work experience records found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <h2 className="text-[#111518] text-[22px] font-bold leading-tight tracking-[-0.015em] px-4 pb-3 pt-5">
              Academic Data
            </h2>

            <div className="px-4 py-3">
              <div className="overflow-x-auto rounded-xl border border-[#dce1e5] bg-white">
                <table className="min-w-full">
                  <thead>
                    <tr className="bg-white">
                      <th className="px-4 py-3 text-left text-[#111518] text-sm font-bold leading-normal w-[400px]">
                        Degree
                      </th>
                      <th className="px-4 py-3 text-left text-[#111518] text-sm font-bold leading-normal w-[400px]">
                        Institution
                      </th>
                      <th className="px-4 py-3 text-left text-[#111518] text-sm font-bold leading-normal w-[400px]">
                        Year
                      </th>
                      <th className="px-4 py-3 text-left text-[#111518] text-sm font-bold leading-normal w-[400px]">
                        Specialization
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {employee.academicData &&
                      employee.academicData.length > 0 ? (
                      employee.academicData.map((aca, index) => (
                        <tr key={index} className="border-t border-[#dce1e5]">
                          <td className="h-[72px] px-4 py-2 text-[#637988] text-sm font-normal leading-normal w-[400px]">
                            {aca.degree}
                          </td>
                          <td className="h-[72px] px-4 py-2 text-[#637988] text-sm font-normal leading-normal w-[400px]">
                            {aca.institution}
                          </td>
                          <td className="h-[72px] px-4 py-2 text-[#637988] text-sm font-normal leading-normal w-[400px]">
                            {aca.year}
                          </td>
                          <td className="h-[72px] px-4 py-2 text-[#637988] text-sm font-normal leading-normal w-[400px]">
                            {aca.specialization}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td
                          colSpan="4"
                          className="px-4 py-3 text-center text-[#637988] text-sm"
                        >
                          No academic records found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const ContactItem = ({ icon, label, value }) => (
  <div className="flex items-center gap-4 bg-white px-4 min-h-[72px] py-2">
    <div className="text-[#111518] flex items-center justify-center rounded-lg bg-[#f0f3f4] shrink-0 size-12">
      {icon}
    </div>
    <div className="flex flex-col justify-center">
      <p className="text-[#111518] text-base font-medium leading-normal line-clamp-1">
        {label}
      </p>
      <p className="text-[#637988] text-sm font-normal leading-normal line-clamp-2">
        {value || "N/A"}
      </p>
    </div>
  </div>
);

const JobDetailItem = ({ icon, label, value }) => (
  <div className="flex items-center gap-4 bg-white px-4 min-h-[72px] py-2">
    <div className="text-[#111518] flex items-center justify-center rounded-lg bg-[#f0f3f4] shrink-0 size-12">
      {icon}
    </div>
    <div className="flex flex-col justify-center">
      <p className="text-[#111518] text-base font-medium leading-normal line-clamp-1">
        {label}
      </p>
      <p className="text-[#637988] text-sm font-normal leading-normal line-clamp-2">
        {value || "N/A"}
      </p>
    </div>
  </div>
);

const InfoItem = ({ label, value, right }) => (
  <div
    className={`flex flex-col gap-1 border-t border-solid border-t-[#dce1e5] py-4 ${right ? "pr-2" : "pl-2"
      }`}
  >
    <p className="text-[#637988] text-sm font-normal leading-normal">{label}</p>
    <p className="text-[#111518] text-sm font-normal leading-normal">
      {value || "N/A"}
    </p>
  </div>
);

export default EmployeeProfile;
